import React, { useCallback, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import ColorPicker from '@groovehq/internal-design-system/lib/components/ColorPicker/ColorPicker'
import { styles as fieldStyles } from '@groovehq/internal-design-system/lib/components/Field/Field.styles'
import {
  text,
  paragraph,
} from '@groovehq/internal-design-system/lib/styles/elements'
import { useChannel } from 'ducks/channels/hooks'
import { selectCurrentChannelCount } from 'ducks/channels/selectors'
import DynamicPortal from 'components/DynamicPortal'
import { promisify } from 'util/promises'
import { useRhfDirtyHold } from 'util/dirtyHolds'
import CONFIG, { DEFAULT_COLORS } from './config'
import { styles } from '../styles'

const ChannelDetailsForm = React.forwardRef(
  (
    {
      channelId,
      channelType,
      formId,
      actionsPortalRef,
      actionsComponent: ActionsComponent,
      actionsComponentAdditionalProps,
      inDrawer = false,
      onSubmit: onSubmitParam = () => {
        return Promise.resolve()
      },
      resetAfterSubmit = false,
      showColorPicker = false,
      showForwardEmail = true,
    },
    forwardedRef
  ) => {
    const { schema: FORM_SCHEMA, showEmail, formEmptyState } = CONFIG[
      channelType
    ]

    const { channel } = useChannel(channelId, { useCachedIfAvailable: true })

    const channelCount = useSelector(selectCurrentChannelCount)

    let defaultValues = {
      ...formEmptyState,
      id: channelId,
      color: DEFAULT_COLORS[channelCount % DEFAULT_COLORS.length],
    }

    if (channel) {
      defaultValues = {
        ...defaultValues,
        name: channel.name,
        email: channel.email,
        color: channel.color,
      }
    }

    const {
      register,
      handleSubmit,
      reset,
      control,
      formState: { errors },
    } = useForm({
      mode: 'all',
      resolver: yupResolver(FORM_SCHEMA),
      defaultValues,
      delayError: 1000,
    })

    const { releaseHold } = useRhfDirtyHold(formId, control)

    const { onChange: colorOnChange, name: colorName } = register('color')
    const registeredEmailField = register('email')
    const registeredNameField = register('name')

    const colorPickerRef = useRef(null)
    const [focusedFields, setFocusedFields] = useState({})

    const handleFocus = useCallback(e => {
      e.persist()
      setFocusedFields(previousState => {
        return {
          ...previousState,
          [e.target.name]: true,
        }
      })
    }, [])

    const handleBlur = useCallback(
      e => {
        e.persist()
        setFocusedFields(previousState => {
          return {
            ...previousState,
            [e.target.name]: false,
          }
        })
        switch (e.target.name) {
          case registeredEmailField.name:
            return registeredEmailField.onBlur(e)
          case registeredNameField.name:
            return registeredNameField.onBlur(e)
          default:
            return null
        }
      },
      [registeredEmailField, registeredNameField]
    )

    const handleColorChange = useCallback(
      colorValue => {
        colorOnChange({
          target: {
            name: colorName,
            value: colorValue,
          },
        })
      },
      [colorOnChange, colorName]
    )

    const onSubmit = useCallback(
      data => {
        if (resetAfterSubmit) {
          return promisify(onSubmitParam, data).then(() => {
            reset(data)
          })
        }
        releaseHold()
        return onSubmitParam(data)
      },
      [resetAfterSubmit, onSubmitParam, reset, releaseHold]
    )

    const onReset = useCallback(
      e => {
        releaseHold()
        reset()
        colorPickerRef.current.setColor(defaultValues.color)
        e.preventDefault()
      },
      [reset, colorPickerRef, defaultValues.color, releaseHold]
    )

    const isForwarding =
      channelType === 'forwarding' || channelType === 'legacy'
    const labelValue =
      channelType === 'facebook' || channelType === 'instagram'
        ? 'The channel display name'
        : `${app.t('Mailbox')} name`

    return (
      <form
        id={formId}
        onSubmit={handleSubmit(onSubmit)}
        onReset={onReset}
        ref={forwardedRef}
      >
        <div>
          <Field
            label={labelValue}
            placeholder="i.e. support"
            className="grui mt-12 channel-details-field"
            css={inDrawer && styles.fullWidth}
            validateStatus={
              !focusedFields.name && errors?.name ? 'error' : undefined
            }
            help={!focusedFields.name ? errors?.name?.message : undefined}
            {...registeredNameField}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {showEmail && (
            <>
              <Field
                label="Email address"
                placeholder="i.e. support@acme.com"
                className="grui mt-12 channel-details-field"
                css={inDrawer && styles.fullWidth}
                validateStatus={
                  !focusedFields.email && errors?.email ? 'error' : undefined
                }
                help={!focusedFields.email ? errors?.email?.message : undefined}
                disabled={!isForwarding}
                {...registeredEmailField}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
              {showForwardEmail &&
                channel && (
                  <p
                    css={[
                      paragraph.styles.preflight,
                      text.styles.textMediumDark,
                    ]}
                    className="grui mt-3"
                  >
                    {isForwarding
                      ? `Forwarded to ${channel.forward_email_address}`
                      : 'Connected via oAuth'}
                  </p>
                )}
            </>
          )}
          {showColorPicker && (
            <>
              <div
                css={[
                  fieldStyles.labelBox,
                  styles.colorLabel,
                  inDrawer && styles.fullWidth,
                ]}
                className="grui mt-12"
              >
                Color
              </div>
              <ColorPicker
                defaultColor={defaultValues.color}
                pickerBtnStyles={styles.colorPickerBtn}
                css={styles.popoverContainer}
                onColorChange={handleColorChange}
                ref={colorPickerRef}
                colorType="rgb"
                dataTestId="button-channel-details-color-picker"
              />
            </>
          )}
          <DynamicPortal portalRef={actionsPortalRef}>
            <ActionsComponent
              {...actionsComponentAdditionalProps}
              control={control}
              formId={formId}
            />
          </DynamicPortal>
        </div>
      </form>
    )
  }
)

export default ChannelDetailsForm
